import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieGrid from "@/components/feature/MovieGrid";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { getHotMovies, searchMovies } from "@/utils/movieUtils";
import type { MovieType } from "@/types/movie";

type TypeFilter = "all" | MovieType;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const typeFilter = (searchParams.get("type") as TypeFilter) || "all";
  const [input, setInput] = useState(keyword);

  const { favorites, toggleFavorite } = useFavorites();

  usePageMeta({
    title: keyword ? `搜索“${keyword}” - 伦理影院` : "搜索影视 - 伦理影院",
    description: "在伦理影院搜索伦理题材电影和电视剧，支持按影片名称、导演、演员或标签模糊搜索，快速找到你想看的伦理影视作品。",
    keywords: "伦理影院,影视搜索,伦理电影,伦理电视剧",
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => searchMovies(keyword, typeFilter), [keyword, typeFilter]);
  const hotMovies = useMemo(() => getHotMovies(6), []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (input.trim()) next.q = input.trim();
    if (typeFilter !== "all") next.type = typeFilter;
    setSearchParams(next);
  };

  const setType = (next: TypeFilter) => {
    const params: Record<string, string> = {};
    if (keyword) params.q = keyword;
    if (next !== "all") params.type = next;
    setSearchParams(params);
  };

  const typeTabs: { value: TypeFilter; label: string }[] = [
    { value: "all", label: "全部" },
    { value: "movie", label: "电影" },
    { value: "tv", label: "电视剧" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8">
        <div className="mx-auto max-w-4xl pt-10 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">搜索影视</h1>
          <p className="mt-3 text-sm text-foreground-600">输入影片名称、导演、演员或标签，快速找到相关伦理影视内容</p>

          <form onSubmit={submit} className="mt-6 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：沉默的抉择、家庭、悬疑"
                className="w-full rounded-md border border-background-300/60 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line"></i> 搜索
            </button>
          </form>

          <div className="mt-5 inline-flex items-center gap-1 rounded-full border border-background-200/70 bg-background-100 p-1">
            {typeTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setType(tab.value)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  typeFilter === tab.value
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {keyword ? (
          <div className="mt-10">
            <p className="text-sm text-foreground-600">
              关键词「<span className="font-semibold text-primary-600">{keyword}</span>」共找到{" "}
              <span className="font-semibold text-accent-600">{results.length}</span> 条结果
            </p>

            {results.length > 0 ? (
              <div className="mt-6">
                <MovieGrid movies={results} favorites={favorites} onToggleFavorite={toggleFavorite} />
              </div>
            ) : (
              <div className="mt-8">
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300/70 bg-background-100 py-16 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-search-eye-line text-3xl"></i>
                  </span>
                  <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-900">未找到相关影视内容</h2>
                  <p className="mt-2 max-w-sm text-sm text-foreground-600">
                    换个关键词试试，或从下方热门影视中挑选一部开始观看。
                  </p>
                </div>
                <div className="mt-10">
                  <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950">热门影视推荐</h2>
                  <MovieGrid movies={hotMovies} favorites={favorites} onToggleFavorite={toggleFavorite} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-10">
            <h2 className="mb-5 font-heading text-xl font-bold text-foreground-950">热门影视推荐</h2>
            <MovieGrid movies={hotMovies} favorites={favorites} onToggleFavorite={toggleFavorite} />
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="text-sm text-foreground-500">热门标签：</span>
              {["伦理", "家庭", "悬疑", "都市", "情感"].map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="whitespace-nowrap rounded-full border border-background-300/60 bg-background-100 px-3 py-1 text-sm text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}